import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Close from "@mui/icons-material/Close";
import { formatDateTime } from "../../../../lib/datetime/displayDateTime";
import {
  buildOriginalEmailDocument,
  formatEmailRecipient,
  type EmailRecipient,
  type OriginalEmailData,
} from "../../../../lib/incident/originalEmailPreview";
import { pageButtonCancelProps } from "../../../../lib/ui/pageButtons";

const metaLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#5f6368",
} as const;

const chipSx = {
  maxWidth: "100%",
  height: "auto",
  color: "#1f1f1f",
  borderColor: "#dadce0",
  bgcolor: "#ffffff",
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    px: 0.75,
    py: 0.25,
    fontSize: "0.7rem",
  },
} as const;

function Recipients({
  label,
  recipients,
}: {
  label: string;
  recipients?: EmailRecipient[] | null;
}) {
  if (!recipients?.length) return null;

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" sx={metaLabelSx}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
        {recipients.map((recipient, index) => {
          const text = formatEmailRecipient(recipient);
          if (!text) return null;
          return <Chip key={`${text}-${index}`} label={text} size="small" variant="outlined" sx={chipSx} />;
        })}
      </Box>
    </Box>
  );
}

export default function OriginalEmailDialog({
  open,
  onClose,
  bodyContent,
  contentType,
  emailData,
}: {
  open: boolean;
  onClose: () => void;
  bodyContent: string;
  contentType?: string | null;
  emailData?: OriginalEmailData | null;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const srcDoc = useMemo(
    () => (bodyContent ? buildOriginalEmailDocument(bodyContent, contentType) : ""),
    [bodyContent, contentType],
  );
  const fromLabel = formatEmailRecipient(emailData?.from);
  const fromInitial = emailData?.from?.emailAddress?.name?.charAt(0)?.toUpperCase() || "?";
  const sentAt = formatDateTime(emailData?.sentDateTime || emailData?.receivedDateTime, "medium");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      aria-labelledby="original-email-dialog-title"
      slotProps={{
        paper: {
          sx: {
            height: { xs: "100%", sm: "min(90vh, 860px)" },
            maxHeight: { xs: "100%", sm: "90vh" },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle
        id="original-email-dialog-title"
        sx={{ position: "relative", display: "flex", alignItems: "flex-start", pr: 6, flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="span" variant="h6" sx={{ display: "block", fontWeight: 600 }}>
            {t("common.originalEmail")}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
            {t("incidents.viewOriginalEmailSubtitle")}
          </Typography>
        </Box>
        <IconButton
          aria-label={t("common.close")}
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "#f1f3f4",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            flexShrink: 0,
            maxHeight: { xs: "40%", sm: 220 },
            overflow: "auto",
            bgcolor: "#f1f3f4",
            color: "#1f1f1f",
            borderBottom: "1px solid #dadce0",
            "& .MuiTypography-root": { color: "#1f1f1f" },
          }}
        >
          {emailData?.subject ? (
            <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", lineHeight: 1.3, mb: 1.25, overflowWrap: "anywhere" }}>
              {emailData.subject}
            </Typography>
          ) : null}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "flex-start" },
              gap: 2,
              minWidth: 0,
            }}
          >
            {fromLabel ? (
              <Box sx={{ minWidth: 0, flexShrink: 0 }}>
                <Typography variant="caption" sx={metaLabelSx}>
                  {t("incidents.from")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, minWidth: 0 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem", bgcolor: "primary.main" }}>
                    {fromInitial}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.85rem", overflowWrap: "anywhere" }}>
                      {emailData?.from?.emailAddress?.name || t("common.unknown")}
                    </Typography>
                    {emailData?.from?.emailAddress?.address ? (
                      <Typography variant="caption" sx={{ color: "#5f6368", overflowWrap: "anywhere" }}>
                        {emailData.from.emailAddress.address}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            ) : null}

            <Recipients label={t("incidents.to")} recipients={emailData?.toRecipients} />
          </Box>

          {emailData?.ccRecipients?.length || emailData?.bccRecipients?.length || sentAt ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.25, minWidth: 0 }}>
              <Recipients label={t("incidents.cc")} recipients={emailData?.ccRecipients} />
              <Recipients label={t("incidents.bcc")} recipients={emailData?.bccRecipients} />
              {sentAt ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={metaLabelSx}>
                    {t("incidents.date")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, fontSize: "0.85rem" }}>
                    {sentAt}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            bgcolor: "#ffffff",
            overflow: "hidden",
          }}
        >
          {srcDoc ? (
            <iframe
              title={t("common.originalEmail")}
              srcDoc={srcDoc}
              sandbox=""
              referrerPolicy="no-referrer"
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                border: 0,
                background: "#ffffff",
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ p: 2, fontStyle: "italic", color: "#5f6368" }}>
              {t("incidents.noOriginalEmailContent")}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0 }}>
        <Button {...pageButtonCancelProps} type="button" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
